import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingController, Platform, ToastController } from '@ionic/angular';
import { CookieService } from 'ngx-cookie-service';
import { BaseClass } from '../shared/classes/base';
import { AuthService } from '../shared/services/auth.service';
import { TranslationService } from '../shared/services/translation.service';
import { ConfigService } from '../shared/services/multi-clients/config.service';
import { AssetService } from '../shared/services/multi-clients/asset.service';
import { BrandingService } from '../shared/services/branding.service';

@Component({
    selector: 'app-login',
    templateUrl: './login.page.html',
    styleUrls: ['./login.page.scss']
})
export class LoginComponent extends BaseClass implements OnInit {

    public loading: boolean;
    public formGroup: UntypedFormGroup;
    public apiUrl = '';
    public standardLogin = false;
    public sso;
    public overrideLogin = false;
    public loginImgPath: string = '';

    constructor(
        protected _toastController: ToastController,
        protected _translationService: TranslationService,
        private _authService: AuthService,
        private router: Router,
        private _activatedRoute: ActivatedRoute,
        private _loadingController: LoadingController,
        private _cookieService: CookieService,
        public platform: Platform,
        private _configService: ConfigService,
        private _assetService: AssetService,
        private _brandingService: BrandingService
    ) {
        super(_toastController, _translationService);

        this.formGroup = new UntypedFormGroup({
            'username': new UntypedFormControl('', [Validators.required]),
            'password': new UntypedFormControl('', [Validators.required])
        });
        if (this._authService.hasToken()) {
            this.router.navigate(['dashboard']);
        }

        this.overrideLogin = this._getQueryString('overrideLogin') === '1';
    }
    async ngOnInit(): Promise<void> {
        this.apiUrl = this._configService.settings.apiUrl;
        this.sso = this._configService.settings.features.sso;
        this.loginImgPath = this._assetService.getLoginImgPath();

        if (this._authService.hasToken()) {
            this.router.navigate(['/dashboard']);
            return;
        }

        if (this.sso) {
            await this.checkAutoSsoByUrl();
        }
    }
    
    public async checkAutoSsoByUrl(): Promise<void> {
        try {
            // Se existe email salvo, o branding já foi aplicado no APP_INITIALIZER
            // Não sobrescrever com branding da URL (precedência: email > hostname)
            const lastEmail = this._brandingService.getLastEmail();
            if (lastEmail) {
                console.log('[Auto-SSO] Skipping hostname branding - email-based branding already applied');
                return;
            }

            const hostname = window.location.hostname;
            const result = await this._brandingService.getBrandingByHostname(hostname).toPromise();

            if (result?.success && result?.data?.found && result?.data?.configId) {
                if (result?.data?.branding) {
                    this._applyBranding(result.data.branding);
                }
            }
        } catch (error) {
            console.error('[Auto-SSO] Detection failed:', error);
            // Silently fail - user can still use email-based login
        }
    }
    
    public async loginWithAutoSso(): Promise<void> {
        const username = this.formGroup.get('username')?.value?.trim();

        if (!username) {
            this.notify(this._translationService.getTranslation('login.validation.emailRequired'), 'warning');
            return;
        }

        // Validate email format
        if (!username.includes('@')) {
            this.notify(this._translationService.getTranslation('login.validation.emailInvalid'), 'warning');
            return;
        }

        this.loading = true;

        // Salva email no localStorage para usar na próxima inicialização
        this._brandingService.saveLastEmail(username);

        try {
            const domainResult = await this._brandingService.checkEmailDomain(username).toPromise();

            let configToUse = null;
            let branding = null;

            if (domainResult?.success && domainResult?.data?.found) {
                configToUse = {
                    configId: domainResult.data.configId,
                    configName: domainResult.data.configName
                };
                branding = domainResult.data.branding;
                console.log('[SSO] Using domain-based config:', configToUse);

                if (branding) {
                    this._applyBranding(branding);
                }
            } else {
                // Fallback to env config (PRIORITY 3)
                console.log('[SSO] Using env default config');
                // Will redirect without configId
            }


            // Redirect to SSO
            const emailParam = `email=${encodeURIComponent(username)}`;
            const ssoUrl = configToUse
                ? `${this.apiUrl}/api/account/login/sso/${configToUse.configId}?${emailParam}`
                : `${this.apiUrl}/api/account/login/sso?${emailParam}`;

            console.log('[SSO] Redirecting to:', ssoUrl);
            window.location.href = ssoUrl;

        } catch (error) {
            console.error('[SSO] Error during login:', error);
            this.loading = false;
            this.notifyError(error);
        }
    }


    private _applyBranding(branding: any): void {
        if (!branding) return;
        
        if (branding.loginScreenImage) {
            this.loginImgPath = branding.loginScreenImage;
        }
    }

    public async forgotPass() {
        const username = this.formGroup.get('username')?.value?.trim();
        if (username) {
            this.loading = true;
            await this._authService.forgotPass(username).subscribe(() => {
                this.loading = false;
                this.notifySuccess('login.forgotPassSuccess');
            }, (error) => {
                this.loading = false;
                this.notifyError(error);
            });
        } else {
            this.notify('Preencha o campo de usuário', 'danger');
        }
    }

    public signUp() {
        this.router.navigate(['register']);
    }

    public async doLogin() {
        this.loading = true;
        const username = this.formGroup.get('username')?.value?.trim();
        const password = this.formGroup.get('password')?.value;

        // Verifica domínio do email e aplica branding (mesmo comportamento do SSO)
        if (username && username.includes('@')) {
            this._brandingService.saveLastEmail(username);
            try {
                const domainResult = await this._brandingService.checkEmailDomain(username).toPromise();
                if (domainResult?.success && domainResult?.data?.found && domainResult?.data?.branding) {
                    this._applyBranding(domainResult.data.branding);
                }
            } catch (error) {
                console.error('[Password Login] Domain check failed:', error);
                // Continua com login mesmo se verificação de branding falhar
            }
        }

        try {
            const result = await this._authService.login(username, password);
            result.success ?
                this._handleLoginSuccess(result) :
                this._handleLoginError(result);

        } catch (error) {
            this.notifyError(error);
            this.loading = false;
        }
    }

    public async loginOAuth() {
        const loading = await this._loadingController.create();
        loading.present();
        this._authService.loginOAuthSSO()
            .then(() => this.router.navigate(['dashboard']))
            .catch((err) => {
                this.notifyError(err);
            }).finally(() => {
                loading.dismiss();
            });
    }

    private _handleLoginSuccess(result): void {
        this.loading = false;
        if (result.data.passwordChangeRequired) {
            window.location.href = '/first-access';
        } else {
            if (this._authService.getRedirectUrl()) {
                const url = this._authService.getRedirectUrl();
                this._authService.clearRedirectUrl();
                window.location.href = url;
            } else {
                window.location.href = '/dashboard';
            }
        }
    }

    private _goRedirectURl() {
        const url = this._authService.getRedirectUrl();
        this._authService.clearRedirectUrl();
        this.router.navigateByUrl(url);
    }

    private _handleLoginError(result): void {
        console.log('Login error:', result);

        if (result.errors?.[0].includes('login.wrongDNS.')) {
            const responseError = result.errors[0].split('./');
            const error = responseError?.[0];
            const host = responseError?.[1]?.length > 0 ? ' [' + responseError?.[1] + ']' : '';
            this.loading = false;
            this.notify(this._translationService.getTranslation(error.replace('./', '')) + host, 'danger');
        }
        else {
            const error = result && result.errors && result.errors.length > 0 ?
                result.errors[0] : 'Houve um erro ao se comunicar com o servidor. Por favor tente mais tarde.';
            this.loading = false;
            this.notify(this._translationService.getTranslation(error), 'danger');
        }
    }

    private _getQueryString(param: string): string | null {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(param);
    }
}
